/**
 * Contexte de gestion du thème (dark / light) + provider qui :
 *  - lit la préférence depuis localStorage (clé `sqseverac.themeMode`)
 *  - par défaut : **dark**
 *  - persiste tout changement
 *  - injecte le `ThemeProvider` MUI et `CssBaseline` (pour appliquer le bg)
 *
 * Usage côté composant :
 *   const { mode, toggle } = useThemeMode();
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { buildTheme } from './theme.js';

const STORAGE_KEY = 'sqseverac.themeMode';

const ThemeModeContext = createContext({
  mode: 'dark',
  toggle: () => {},
});

export function ThemeModeProvider({ children }) {
  const [mode, setMode] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored === 'light' || stored === 'dark' ? stored : 'dark';
    } catch {
      return 'dark';
    }
  });

  const toggle = useCallback(() => {
    setMode((m) => {
      const next = m === 'dark' ? 'light' : 'dark';
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch {
        // localStorage indisponible : on ne persiste pas, le toggle reste en RAM.
      }
      return next;
    });
  }, []);

  const theme = useMemo(() => buildTheme(mode), [mode]);
  const ctx = useMemo(() => ({ mode, toggle }), [mode, toggle]);

  // Synchronise un attribut `data-theme` sur <html> pour permettre au CSS
  // global (styles.css → fond du body avec photo et voiles) de réagir au mode.
  useEffect(() => {
    document.documentElement.dataset.theme = mode;
  }, [mode]);

  return (
    <ThemeModeContext.Provider value={ctx}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
}

export function useThemeMode() {
  return useContext(ThemeModeContext);
}
